import { useState } from 'react';
import { queryClient, queryKeys } from './query-client';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { uploadAttachment } from './attachments';
import { fileUploadStrategy, uniqueFileName, MAX_WRITABLE_CONTENT_CHARS } from './file-edit';
import { writeKeyOf, type WriteDirectory } from './file-browser';

// Adding files a user already has — the Files screen's Upload, and dropping them onto the browser.
//
// TWO JOURNEYS, and `fileUploadStrategy` picks between them per file:
//
//   inline    — a text file within the inline cap. Read it here, write it as content. ONE request, no
//               media at all, and the file lands editable by the pencil.
//   reference — everything else. `uploadAttachment` mints an upload URL and PUTs the bytes to WixMP
//               (the same path chat attachments take, reused rather than rebuilt), then the file is
//               written as a pointer to them.
//
// The batch is deliberately SEQUENTIAL and per-file fault-tolerant: dropping twelve files should not fire
// twelve concurrent uploads, and one refusal in the middle must not cost the other eleven. Each result is
// reported on its own; the caller shows what landed and what did not.

export interface FileUploadOutcome {
  name: string;
  ok: boolean;
  /** Why this one failed, when the server or the uploader named a reason worth showing. */
  error?: string;
}

export type FileUploadState = 'idle' | 'uploading';

/** Reads a text file as a string, or null when it cannot be decoded as text after all. */
async function readText(file: File): Promise<string | null> {
  try {
    return await file.text();
  } catch {
    return null;
  }
}

export function useFileUpload() {
  const [state, setState] = useState<FileUploadState>('idle');
  /** How far through a batch we are, so the caller can say "3 of 12" rather than just spinning. */
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);

  const writeFile = async (body: Record<string, unknown>, directory?: WriteDirectory): Promise<string | null> => {
    const res = await apiFetch(`${API_BASE}/api/write-file`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ ...body, create: true, ...(directory ? { directory } : {}) }),
    });
    if (res.ok) return null;
    return await res
      .json()
      .then((payload) => payload?.error ?? 'failed')
      .catch(() => 'failed');
  };

  /**
   * Uploads `files` into `folder` (a full `workspaces` path — `<workspaceId>` or `<workspaceId>/<sub>`).
   *
   * `existingNames` are the file names already in that folder, used to number a collision rather than
   * fail on it. The server still asserts `create`, so a name that races is refused rather than
   * overwriting — this only keeps that refusal off the common path.
   */
  const upload = async (
    files: File[],
    folder: string,
    existingNames: string[],
    directory?: WriteDirectory,
    /** A single file the user named and described before confirming — the upload draft dialog. When
     *  present it REPLACES the file's own name, and its hint rides the write, so an uploaded file can be
     *  filed as deliberately as an authored one. Only meaningful for a one-file call. */
    draft?: { name: string; hint: string },
  ): Promise<FileUploadOutcome[]> => {
    if (files.length === 0 || !folder) return [];
    setState('uploading');
    setProgress({ done: 0, total: files.length });

    // Names claimed as we go, so two files dropped in the same batch with the same name do not both
    // resolve to the same free slot.
    const claimed = [...existingNames];
    const outcomes: FileUploadOutcome[] = [];

    for (const file of files) {
      const name = uniqueFileName(draft && files.length === 1 ? draft.name : file.name, claimed);
      // Only sent when there is one — `hint` is a proto3 plain string, so an empty one is
      // indistinguishable from absent on the wire and the server reads absent as "keep".
      const hintBody = draft?.hint ? { hint: draft.hint } : {};
      claimed.push(name);
      // The KEY inside the directory, not the tree path: an agent's folder is `brain/<agent>/…` in the
      // browser and `<agent>/…` in the store, and writing the tree path nested a second `brain` inside
      // the first. See `writeKeyOf`.
      const path = writeKeyOf(`${folder}/${name}`);

      try {
        if (fileUploadStrategy(name, file.size) === 'inline') {
          const text = await readText(file);
          // A multi-byte file can pass the byte-size test and still exceed the CHARACTER cap, so the
          // real length is checked once it is read — and it falls through to the reference path rather
          // than failing, because the file is perfectly uploadable, just not inline.
          if (text !== null && text.length <= MAX_WRITABLE_CONTENT_CHARS) {
            const error = await writeFile({ path, content: text, ...hintBody }, directory);
            outcomes.push({ name, ok: !error, ...(error ? { error } : {}) });
            setProgress({ done: outcomes.length, total: files.length });
            continue;
          }
        }

        const uploaded = await uploadAttachment(file);
        if (!uploaded.ok) {
          outcomes.push({ name, ok: false, error: uploaded.kind });
          setProgress({ done: outcomes.length, total: files.length });
          continue;
        }
        const url = uploaded.attachment.url;
        if (!url) {
          outcomes.push({ name, ok: false, error: 'failed' });
          setProgress({ done: outcomes.length, total: files.length });
          continue;
        }
        const error = await writeFile(
          {
            path,
            reference: { url },
            contentType: file.type || undefined,
            sizeBytes: file.size,
            ...hintBody,
          },
          directory,
        );
        outcomes.push({ name, ok: !error, ...(error ? { error } : {}) });
      } catch {
        outcomes.push({ name, ok: false, error: 'failed' });
      }
      setProgress({ done: outcomes.length, total: files.length });
    }

    // One refresh for the batch rather than one per file. The per-workspace list AND the account-wide
    // one, which is its own cache entry that a per-workspace key never reaches.
    const workspaceId = folder.split('/')[0];
    await queryClient.invalidateQueries({ queryKey: queryKeys.workspaceFiles(workspaceId) });
    await queryClient.invalidateQueries({ queryKey: queryKeys.allWorkspaceFiles });

    setState('idle');
    setProgress(null);
    return outcomes;
  };

  return { state, progress, upload };
}
